export type HeyDexMcpToolSummaryAction =
  | "capabilities"
  | "delegate"
  | "task-status"
  | "task-cancel"
  | "thread-create"
  | "thread-list"
  | "thread-read"
  | "thread-send"
  | "thread-wait"
  | "thread-interrupt"
  | "thread-configuration"
  | "thread-configure"
  | "thread-fork"
  | "thread-search"
  | "thread-transfers"
  | "thread-organize"
  | "thread-update"
  | "queue-list"
  | "queue-read"
  | "queue-edit"
  | "queue-cancel"
  | "queue-reorder"
  | "queue-steer"
  | "question-list"
  | "question-read"
  | "question-respond"
  | "project-list"
  | "project-read"
  | "project-create"
  | "project-update"
  | "project-delete"
  | "attachment-prepare"
  | "attachment-discard"
  | "attachment-send"
  | "record"
  | "library";

export interface HeyDexMcpToolDefinition {
  readonly labels: readonly [action: string, running: string, completed: string, detail: string];
  readonly summaryAction: HeyDexMcpToolSummaryAction;
}

function tool(
  labels: HeyDexMcpToolDefinition["labels"],
  summaryAction: HeyDexMcpToolSummaryAction,
): HeyDexMcpToolDefinition {
  return { labels, summaryAction };
}

// Cards, activity rows, summaries, and provider identity recovery share this inventory.
const HEYDEX_MCP_TOOLS: Readonly<Record<string, HeyDexMcpToolDefinition>> = {
  orchestrator_capabilities: tool(
    ["Get", "Getting", "Got", "orchestration capabilities"],
    "capabilities",
  ),
  delegate_task: tool(["Delegate", "Delegating", "Delegated", "a child task"], "delegate"),
  task_status: tool(["Get", "Getting", "Got", "delegated task status"], "task-status"),
  task_cancel: tool(
    ["Cancel", "Canceling", "Requested cancellation of", "delegated task"],
    "task-cancel",
  ),
  create_threads: tool(["Create", "Creating", "Created", "HeyDex threads"], "thread-create"),
  heydex_thread_list: tool(["List", "Listing", "Listed", "HeyDex threads"], "thread-list"),
  heydex_thread_read: tool(["Read", "Reading", "Read", "a HeyDex thread"], "thread-read"),
  heydex_thread_send: tool(["Send", "Sending", "Sent", "to a HeyDex thread"], "thread-send"),
  heydex_thread_wait: tool(["Wait", "Waiting", "Waited", "for a HeyDex thread"], "thread-wait"),
  heydex_thread_interrupt: tool(
    ["Interrupt", "Interrupting", "Requested an interrupt of", "a HeyDex thread"],
    "thread-interrupt",
  ),
  heydex_queue_list: tool(["List", "Listing", "Listed", "queued messages"], "queue-list"),
  heydex_queue_read: tool(["Read", "Reading", "Read", "a queued message"], "queue-read"),
  heydex_queue_edit: tool(["Edit", "Editing", "Edited", "a queued message"], "queue-edit"),
  heydex_queue_cancel: tool(
    ["Cancel", "Canceling", "Requested cancellation of", "a queued run"],
    "queue-cancel",
  ),
  heydex_queue_reorder: tool(
    ["Reorder", "Reordering", "Reordered", "a queued run"],
    "queue-reorder",
  ),
  heydex_queue_promote_to_steer: tool(
    ["Steer with", "Steering with", "Requested steering with", "a queued message"],
    "queue-steer",
  ),
  heydex_pending_request_list: tool(
    ["List", "Listing", "Listed", "pending questions"],
    "question-list",
  ),
  heydex_pending_request_read: tool(
    ["Read", "Reading", "Read", "pending questions"],
    "question-read",
  ),
  heydex_pending_request_respond: tool(
    ["Answer", "Answering", "Answered", "pending questions"],
    "question-respond",
  ),
  heydex_thread_configuration: tool(
    ["Read", "Reading", "Read", "thread configuration"],
    "thread-configuration",
  ),
  // HeyDay's record and the user's library.
  heyday_guide: tool(["Read", "Reading", "Read", "HeyDay's guide to its record"], "record"),
  heyday_search: tool(["Search", "Searching", "Searched", "HeyDay's record"], "record"),
  heyday_list: tool(["List", "Listing", "Listed", "from HeyDay's record"], "record"),
  heyday_get: tool(["Read", "Reading", "Read", "a record"], "record"),
  heyday_count: tool(["Count", "Counting", "Counted", "in HeyDay's record"], "record"),
  heyday_calendar: tool(["Check", "Checking", "Checked", "what falls due"], "record"),
  heyday_changes: tool(
    ["Check", "Checking", "Checked", "what changed in HeyDay's record"],
    "record",
  ),
  heyday_recipe: tool(["Read", "Reading", "Read", "a recipe"], "record"),
  heyday_pull: tool(["Pull", "Pulling", "Pulled", "records into the ledger"], "record"),
  heyday_track: tool(["Update", "Updating", "Updated", "what is tracked"], "record"),
  heyday_contribute: tool(["Send", "Sending", "Sent", "leads to HeyDay News"], "record"),
  ledger_query: tool(["Query", "Querying", "Queried", "the ledger"], "record"),
  library: tool(["Check", "Checking", "Checked", "your library"], "library"),
  library_add: tool(["Add", "Adding", "Added", "to your library"], "library"),
  library_match: tool(
    ["Compare", "Comparing", "Compared", "your documents with the record"],
    "library",
  ),
  // The computer the agent works on, where HeyDex runs on one.
  heydex_thread_configure: tool(["Set", "Setting", "Set", "thread model"], "thread-configure"),
  heydex_thread_fork: tool(
    ["Fork", "Forking", "Requested a fork of", "this thread"],
    "thread-fork",
  ),
  heydex_thread_search: tool(
    ["Search", "Searching", "Searched", "thread content"],
    "thread-search",
  ),
  heydex_thread_transfers: tool(
    ["Read", "Reading", "Read", "thread transfers"],
    "thread-transfers",
  ),
  heydex_thread_organize: tool(
    ["Organize", "Organizing", "Organized", "a thread"],
    "thread-organize",
  ),
  heydex_thread_update: tool(
    ["Update", "Updating", "Updated", "HeyDex thread metadata"],
    "thread-update",
  ),
  heydex_thread_launch: tool(
    ["Launch", "Launching", "Launched", "a thread in a folder"],
    "thread-create",
  ),
  heydex_project_list: tool(["List", "Listing", "Listed", "folders"], "project-list"),
  heydex_project_read: tool(["Read", "Reading", "Read", "a folder"], "project-read"),
  heydex_project_create: tool(
    ["Register", "Registering", "Registered", "a folder"],
    "project-create",
  ),
  heydex_project_update: tool(["Update", "Updating", "Updated", "a folder"], "project-update"),
  heydex_project_delete: tool(["Delete", "Deleting", "Deleted", "a folder"], "project-delete"),
  heydex_attachment_prepare_upload: tool(
    ["Prepare", "Preparing", "Prepared", "an attachment upload"],
    "attachment-prepare",
  ),
  heydex_attachment_discard: tool(
    ["Discard", "Discarding", "Discarded", "a pending attachment"],
    "attachment-discard",
  ),
  heydex_thread_send_attachments: tool(
    ["Send", "Sending", "Sent", "attachments"],
    "attachment-send",
  ),
};

function normalizeHeyDexMcpToolLabel(value: string): string {
  return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}

function resolveHeyDexMcpToolName(value: string): string | null {
  const label = normalizeHeyDexMcpToolLabel(value);
  return Object.hasOwn(HEYDEX_MCP_TOOLS, label) ? label : null;
}

export function resolveHeyDexMcpToolDefinition(
  toolName: string | null | undefined,
): HeyDexMcpToolDefinition | null {
  const name = toolName == null ? null : resolveHeyDexMcpToolName(toolName);
  return name !== null && Object.hasOwn(HEYDEX_MCP_TOOLS, name) ? HEYDEX_MCP_TOOLS[name]! : null;
}

export function resolveHeyDexMcpToolSummaryAction(
  toolName: string | null | undefined,
): HeyDexMcpToolSummaryAction | null {
  return resolveHeyDexMcpToolDefinition(toolName)?.summaryAction ?? null;
}
